import React, { useEffect, useState, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X, Trophy, MapPin, LogOut, Settings, Ticket, ChevronDown, ChevronUp, Bell, Video, Eye, Trash2, Loader, AlertTriangle, Shield, Info } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import AffiliationSection from '@/components/AffiliationSection';
import DeleteAccountDialog from '@/components/DeleteAccountDialog';

const createPageUrl = (pageName) => {
    const [base, query] = pageName.split('?');
    return query ? `/${base}?${query}` : `/${base}`;
};

export default function Profile({ onClose, profileUserId }) {
          const { user, logout, isLoading } = useAuth();
          const [showAllXP, setShowAllXP] = useState(false);
          const navigate = useNavigate();
          const [notificationsEnabled, setNotificationsEnabled] = useState(user?.notifications_enabled ?? true);
          const [selectedHighlight, setSelectedHighlight] = useState(null);
          const [deleteConfirm, setDeleteConfirm] = useState(null); // highlight to delete
          const [deleting, setDeleting] = useState(false);
          const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
          const viewTimerRef = useRef(null);

          // Support viewing other user profiles
          const viewingUserId = profileUserId || user?.id;
          const isOwnProfile = !profileUserId || profileUserId === user?.id;

    // Auto-scroll to bottom on mobile
    useEffect(() => {
        if (window.innerWidth < 768) {
            setTimeout(() => {
                window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
            }, 100);
        }
    }, []);

    const { data: territories = [] } = useQuery({
        queryKey: ['myTerritories', user?.id],
        queryFn: async () => {
            const { data: playgrounds } = await supabase.from('playgrounds').select('*').eq('owner_user_id', user.id).eq('is_active', true).limit(200);

            return (playgrounds || []).map(playground => ({
                playground_id: playground.id,
                playground: playground
            }));
        },
        enabled: !!user,
        refetchOnWindowFocus: true,
        staleTime: 0
    });

    const { data: xpEvents = [] } = useQuery({
        queryKey: ['xpHistory', user?.id],
        queryFn: async () => {
            const events = await base44.entities.XPEvents.filter(
                { user_id: user.id },
                '-created_at',
                10
            );
            return events;
        },
        enabled: !!user,
        refetchOnWindowFocus: true,
        staleTime: 0
    });

    const { data: raffleEntries = [] } = useQuery({
        queryKey: ['raffleEntries', user?.id],
        queryFn: async () => {
            const now = new Date();
            const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            const entries = await base44.entities.RaffleEntry.filter({
                user_id: user.id,
                raffle_month: currentMonth
            });
            return entries;
        },
        enabled: !!user,
        refetchOnWindowFocus: true,
        staleTime: 0
    });

    const { data: userHighlights = [], refetch: refetchHighlights } = useQuery({
        queryKey: ['userHighlights', viewingUserId],
        queryFn: async () => {
            const highlights = await base44.entities.CourtHighlight.filter({
                created_by_user_id: viewingUserId,
                mux_status: 'READY'
            });
            return (highlights || [])
                .sort((a, b) => new Date(b.created_date) - new Date(a.created_date));
        },
        enabled: !!viewingUserId,
        refetchOnWindowFocus: true,
        staleTime: 0
    });

    const handleNotificationToggle = async (enabled) => {
        setNotificationsEnabled(enabled);
        try {
            const { data: { user: authUser } } = await supabase.auth.getUser();
            if (authUser) await supabase.from('users').update({ notifications_enabled: enabled }).eq('id', authUser.id);
        } catch (error) {
            console.error('Failed to update notification preference:', error);
            setNotificationsEnabled(!enabled);
        }
    };

    const handleHighlightClick = (highlight) => {
        setSelectedHighlight(highlight);
        if (viewTimerRef.current) clearTimeout(viewTimerRef.current);
        viewTimerRef.current = setTimeout(async () => {
            viewTimerRef.current = null;
            try {
                await supabase.from('court_highlights').update({ views_count: (highlight.views_count || 0) + 1 }).eq('id', highlight.id);
                refetchHighlights();
            } catch (error) {
                console.error('Failed to increment view:', error);
            }
        }, 3000);
    };

    const handleCloseHighlight = () => {
        if (viewTimerRef.current) {
            clearTimeout(viewTimerRef.current);
            viewTimerRef.current = null;
        }
        setSelectedHighlight(null);
    };

    const handleDeleteHighlight = async () => {
        if (!deleteConfirm) return;
        setDeleting(true);
        try {
            const { error: delError } = await supabase.from('court_highlights').update({ hidden_at: new Date().toISOString() }).eq('id', deleteConfirm.id);
            if (delError) throw delError;
            const res = { data: { success: true } };
            if (res.data?.error) {
                console.error('Delete highlight error:', res.data.error);
                alert(res.data.error);
                return;
            }
            setDeleteConfirm(null);
            refetchHighlights();
        } catch (error) {
            console.error('Failed to delete highlight:', error);
            alert('Erreur lors de la suppression');
        } finally {
            setDeleting(false);
        }
    };



    if (isLoading || !user) {
        return (
            <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6E2BFF]"></div>
            </div>
        );
    }

    const content = (
            <>
            <div className="p-6">
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-xl font-bold text-white">Profil</h2>
                    {onClose && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onClose}
                            className="text-white hover:text-[#6E2BFF]"
                        >
                            <X className="w-5 h-5" />
                        </Button>
                    )}
                </div>

                <div className="space-y-6 max-w-2xl mx-auto">
                {/* User Info */}
                <Card className="p-6">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
                        <div className="min-w-0 flex-1">
                            <h2 className="text-xl font-bold text-white break-words">{user.username || user.email.split('@')[0]}</h2>
                            <p className="text-[#9CA0AB] text-sm mt-1 break-all">{user.email}</p>
                        </div>
                        <div className="flex flex-col gap-2 items-start md:items-end w-full md:w-auto">
                            {user.role === 'admin' && (
                                 <button
                                     className="bg-[#6E2BFF] text-white text-xs px-3 py-1.5 rounded font-semibold hover:bg-[#FF5C82] transition-colors"
                                     onClick={() => {
                                         if (onClose) onClose();
                                         navigate('/admin');
                                     }}
                                 >
                                     ADMIN
                                 </button>
                             )}
                            <Button
                                size="sm"
                                variant="ghost"
                                className="text-[#9CA0AB] hover:text-white transition-colors p-2 text-sm"
                                onClick={logout}
                            >
                                <LogOut className="w-4 h-4 mr-1" />
                                Déconnexion
                            </Button>
                        </div>
                    </div>
                    
                    <div className="pt-6 border-t border-[#1B1E28] space-y-6">
                        {/* Level + Progress Bar */}
                        {(() => {
                            const level = user.level || 1;
                            const xpTotal = user.xp_total || 0;
                            const xpForCurrentLevel = ((level - 1) ** 2) * 100;
                            const xpForNextLevel = (level ** 2) * 100;
                            const xpInLevel = xpTotal - xpForCurrentLevel;
                            const xpNeeded = xpForNextLevel - xpForCurrentLevel;
                            const progress = xpNeeded > 0 ? Math.min(100, Math.round((xpInLevel / xpNeeded) * 100)) : 100;
                            const xpRemaining = Math.max(0, xpForNextLevel - xpTotal);

                            return (
                                <div>
                                    <div className="flex items-end justify-between mb-3">
                                        <div>
                                            <div className="flex items-center gap-1 mb-1">
                                                <p className="text-sm text-[#9CA0AB]">Niveau</p>
                                                <Popover>
                                                    <PopoverTrigger asChild>
                                                        <button className="text-[#9CA0AB] hover:text-[#6E2BFF] transition-colors">
                                                            <Info className="w-3.5 h-3.5" />
                                                        </button>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-72 bg-[#1B1E28] border-[#6E2BFF]/20 text-white p-4" side="bottom" align="start">
                                                        <div className="space-y-3">
                                                            <div>
                                                                <h4 className="font-semibold text-sm text-white mb-1">Comment ca marche ?</h4>
                                                                <p className="text-xs text-[#9CA0AB]">
                                                                    Tu gagnes de l'XP en <span className="text-white">remportant des duels</span> et en <span className="text-white">ajoutant des terrains</span>. Chaque niveau demande progressivement plus d'XP.
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <h4 className="font-semibold text-sm text-white mb-1">Pourquoi monter de niveau ?</h4>
                                                                <ul className="text-xs text-[#9CA0AB] space-y-1">
                                                                    <li>- Ajouter <span className="text-white">plus de terrains</span></li>
                                                                    <li>- Debloquer <span className="text-white">plus d'invitations</span></li>
                                                                    <li>- Acceder a des <span className="text-white">challenges exclusifs</span></li>
                                                                </ul>
                                                            </div>
                                                            <p className="text-[10px] text-[#6E2BFF] italic">Continue a jouer pour dominer les playgrounds !</p>
                                                        </div>
                                                    </PopoverContent>
                                                </Popover>
                                            </div>
                                            <p className="text-4xl font-bold text-[#6E2BFF]">{level}</p>
                                        </div>
                                        <p className="text-xs text-[#9CA0AB] mb-1">
                                            {xpRemaining} XP avant niveau {level + 1}
                                        </p>
                                    </div>
                                    <div className="w-full h-3 bg-[#1B1E28] rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-[#6E2BFF] to-[#8B5CFF] rounded-full transition-all duration-500"
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                    <div className="flex justify-between mt-1">
                                        <p className="text-[10px] text-[#9CA0AB]">{xpInLevel} / {xpNeeded} XP</p>
                                        <p className="text-[10px] text-[#9CA0AB]">{progress}%</p>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* XP Stats */}
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <p className="text-xs text-[#9CA0AB] mb-1">XP Total</p>
                                <p className="text-xl font-bold text-white">{user.xp_total || 0}</p>
                            </div>
                            <div>
                                <p className="text-xs text-[#9CA0AB] mb-1">Semaine</p>
                                <p className="text-lg font-bold text-white">{user.xp_week || 0}</p>
                            </div>
                            <div>
                                <p className="text-xs text-[#9CA0AB] mb-1">Aujourd'hui</p>
                                <p className="text-lg font-bold text-white">{user.xp_day || 0}</p>
                            </div>
                        </div>

                        {/* Courts */}
                         <div className="pt-4 border-t border-[#1B1E28] flex items-center justify-between">
                             <p className="text-sm text-[#9CA0AB]">Terrains capturés</p>
                             <p className="text-2xl font-bold text-white">{territories.length}</p>
                         </div>

                        {/* Tickets */}
                         <div className="pt-4 border-t border-[#1B1E28] flex items-center justify-between">
                             <div className="flex items-center gap-2">
                                 <Ticket className="w-4 h-4 text-[#6E2BFF]" />
                                 <p className="text-sm text-[#9CA0AB]">Chances ce mois</p>
                             </div>
                             <p className="text-2xl font-bold text-[#6E2BFF]">{raffleEntries.length}</p>
                         </div>
                        

                    </div>
                </Card>

                {/* Notification Settings */}
                <Card className="p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Bell className="w-5 h-5 text-[#6E2BFF]" />
                            <div>
                                <h3 className="font-semibold text-white">Notifications</h3>
                                <p className="text-xs text-[#9CA0AB]">Recevoir les alertes de challenges</p>
                            </div>
                        </div>
                        <Switch
                            checked={notificationsEnabled}
                            onCheckedChange={handleNotificationToggle}
                            className="data-[state=checked]:bg-[#6E2BFF]"
                        />
                    </div>
                </Card>

                {/* XP History */}
                {xpEvents.length > 0 && (
                    <div>
                        <div className="flex items-center justify-between mb-4 gap-2">
                            <h3 className="text-base font-semibold text-white">Historique XP</h3>
                            {xpEvents.length > 3 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowAllXP(!showAllXP)}
                                    className="text-[#B3B6C2] hover:text-[#6E2BFF] transition-colors p-1"
                                >
                                    {showAllXP ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </Button>
                            )}
                        </div>
                        
                        <Card className="divide-y divide-[#1B1E28]">
                            {(showAllXP ? xpEvents : xpEvents.slice(0, 3)).map((event) => (
                                <div key={event.id} className="p-4 flex items-center justify-between gap-4">
                                    <div className="min-w-0 flex-1">
                                        <p className="text-xs font-medium text-white">
                                            {event.source === 'duel_win' && 'Victoire en Duel'}
                                            {event.source === 'duel_defeat' && 'Défaite en Duel'}
                                            {event.source === 'court_conquest' && 'Conquête de terrain'}
                                            {event.source === 'court_defense' && 'Défense de terrain'}
                                            {event.source === 'first_time_court' && 'Premier terrain'}
                                            {event.source === 'new_court_bonus' && 'Bonus nouveau terrain'}
                                            {event.source === 'court_validated' && 'Terrain validé'}
                                            {event.source === 'court_created' && 'Terrain créé'}
                                            {event.source === 'own_court_activity' && 'Activité sur ton terrain'}
                                            {event.source === 'daily_session_bonus' && 'Bonus session quotidienne'}
                                            {event.source === 'return_bonus' && 'Bonus de retour'}
                                            {event.source === 'affiliation_affiliate' && 'Affiliation'}
                                            {event.source === 'affiliation_referrer' && 'Affiliation'}
                                            {!['duel_win', 'duel_defeat', 'court_conquest', 'court_defense', 'first_time_court', 'new_court_bonus', 'court_validated', 'court_created', 'own_court_activity', 'daily_session_bonus', 'return_bonus', 'affiliation_affiliate', 'affiliation_referrer'].includes(event.source) && event.source}
                                        </p>
                                        <p className="text-xs text-[#9CA0AB] mt-1">
                                            {new Date(event.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <p className="text-lg font-bold text-[#6E2BFF] flex-shrink-0">+{event.xp_amount}</p>
                                </div>
                            ))}
                        </Card>
                    </div>
                )}

                {/* My Territories */}
                <div>
                    <h3 className="text-base font-semibold text-white mb-4">Mes terrains</h3>
                    
                    {territories.length === 0 ? (
                        <Card className="p-6 text-center">
                            <p className="text-[#9CA0AB] mb-4">Aucun terrain capturé</p>
                            <Button 
                                className="bg-[#6E2BFF] text-white font-medium"
                                onClick={() => {
                                    if (onClose) onClose();
                                    if (window.__onFindDuelsClick) window.__onFindDuelsClick();
                                }}
                            >
                                Découvrir les terrains
                            </Button>
                        </Card>
                    ) : (
                        <div className="space-y-3">
                            {territories.map((territory) => (
                                <div
                                    key={territory.id}
                                    onClick={() => {
                                        if (onClose) onClose();
                                        if (window.__onPlaygroundSelect && territory.playground) {
                                            window.__onPlaygroundSelect(territory.playground);
                                        } else {
                                            navigate(`/court/${territory.playground_id}`);
                                        }
                                    }}
                                >
                                    <Card className="p-4 hover:bg-[#1B1E28] transition-all cursor-pointer">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-semibold text-white text-base truncate">{territory.playground?.name || 'Terrain'}</h4>
                                                <p className="text-sm text-[#9CA0AB] mt-1 truncate">
                                                    {territory.playground?.city || ''}
                                                </p>
                                            </div>
                                            <Badge className="bg-[#6E2BFF] text-white text-xs flex-shrink-0">
                                                Capturé
                                            </Badge>
                                        </div>
                                    </Card>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Traces Section — always visible */}
                <div>
                    <h3 className="text-base font-semibold text-white mb-4">Traces</h3>
                    {userHighlights.length > 0 ? (
                        <Card className="p-4">
                            <div className="grid grid-cols-2 gap-3">
                                {userHighlights.map((highlight) => (
                                    <div
                                        key={highlight.id}
                                        className="relative cursor-pointer group"
                                        onClick={() => handleHighlightClick(highlight)}
                                    >
                                        <div className="relative aspect-video bg-[#1B1E28] rounded-lg overflow-hidden border border-[#6E2BFF]/30 group-hover:border-[#6E2BFF] transition-all">
                                            {highlight.thumbnail_url ? (
                                                <img
                                                    src={highlight.thumbnail_url}
                                                    alt="Highlight"
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Video className="w-8 h-8 text-[#6E2BFF]/50" />
                                                </div>
                                            )}
                                            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all flex items-center justify-center">
                                                <div className="w-10 h-10 rounded-full bg-[#6E2BFF] flex items-center justify-center">
                                                    <div className="w-0 h-0 border-t-6 border-t-transparent border-l-10 border-l-white border-b-6 border-b-transparent ml-1" />
                                                </div>
                                            </div>
                                            {highlight.duration_sec && (
                                                <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 rounded text-xs text-white">
                                                    {Math.floor(highlight.duration_sec / 60)}:{String(Math.floor(highlight.duration_sec % 60)).padStart(2, '0')}
                                                </div>
                                            )}
                                            {highlight.visibility_scope === 'PROFILE_ONLY' && (
                                                <div className="absolute top-2 left-2 bg-amber-600/80 px-2 py-1 rounded text-xs text-white">
                                                    Privé
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center justify-between mt-1">
                                            <div className="flex items-center gap-1 text-xs text-[#B3B6C2]">
                                                <Eye className="w-3 h-3" />
                                                <span>{highlight.views_count || 0}</span>
                                            </div>
                                            {isOwnProfile && highlight.created_by_user_id === user?.id && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(highlight); }}
                                                    className="text-red-400 hover:text-red-300 p-1"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    ) : (
                        <Card className="p-6 text-center">
                            <Video className="w-8 h-8 text-[#6E2BFF]/30 mx-auto mb-2" />
                            <p className="text-[#9CA0AB] text-sm">Aucune trace pour l'instant</p>
                        </Card>
                    )}
                </div>

                {/* Affiliation Section */}
                <Card className="p-6">
                    <h3 className="text-base font-semibold text-white mb-4">Affiliation</h3>
                    <AffiliationSection user={user} />
                </Card>

                {/* Privacy Link */}
                {isOwnProfile && (
                    <div className="text-center">
                        <Link 
                            to={createPageUrl('Privacy')} 
                            className="inline-flex items-center gap-1.5 text-xs text-[#9CA0AB] hover:text-[#6E2BFF] transition-colors"
                        >
                            <Shield className="w-3.5 h-3.5" />
                            Politique de confidentialité
                        </Link>
                    </div>
                )}

                {/* Delete Account Section */}
                {isOwnProfile && (
                    <Card className="p-6 border-red-600/30">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <h3 className="text-base font-semibold text-red-400 mb-2">Zone de danger</h3>
                                <p className="text-[#B3B6C2] text-xs mb-4">
                                    La suppression de votre compte est définitive et irréversible.
                                </p>
                                <Button
                                    variant="outline"
                                    className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white transition-all w-full"
                                    onClick={() => navigate('/delete-account')}
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Supprimer mon compte
                                </Button>
                            </div>
                        </div>
                    </Card>
                )}

            </div>

                {/* Version */}
                <div className="text-center py-6">
                    <p className="text-[#6B6F7B] text-xs">V1.0.0</p>
                </div>
            </div>

            {/* Delete Account Dialog */}
            <DeleteAccountDialog
                open={showDeleteAccountDialog}
                onOpenChange={setShowDeleteAccountDialog}
                userEmail={user?.email}
                onConfirm={async () => {
                    try {
                        const { data: { user: authUser } } = await supabase.auth.getUser();
                        const { error: delAccErr } = await supabase.from('users').update({ access_status: 'DELETED' }).eq('id', authUser.id);
                        if (delAccErr) throw delAccErr;

                        // Account deleted successfully
                        // Close dialog first to prevent UI issues
                        setShowDeleteAccountDialog(false);

                        // Clear local storage/session data
                        try {
                            localStorage.removeItem('zno:mapState');
                            localStorage.removeItem('zno:feedScroll');
                            sessionStorage.clear();
                        } catch (_) { /* ignore storage errors */ }

                        // Redirect to login page after logout
                        await supabase.auth.signOut();
                        window.location.href = '/login';

                    } catch (error) {
                        console.error('[DeleteAccount] Error:', error);

                        // User-friendly error message
                        const message = error.message?.includes('Unauthorized')
                            ? 'Session expirée. Veuillez vous reconnecter et réessayer.'
                            : error.message?.includes('network') || error.message?.includes('fetch')
                            ? 'Erreur réseau. Vérifiez votre connexion et réessayez.'
                            : error.message || 'Une erreur est survenue lors de la suppression du compte';

                        alert(message);
                    }
                }}
            />

            {/* Highlight Video Player Modal (HLS-compatible) */}
            {selectedHighlight && (
                <ProfileHighlightPlayer
                    highlight={selectedHighlight}
                    onClose={handleCloseHighlight}
                />
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => !deleting && setDeleteConfirm(null)}>
                    <div className="bg-[#1B1E28] rounded-xl p-6 max-w-sm w-full space-y-4" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-white text-lg font-semibold">Supprimer le highlight ?</h3>
                        <p className="text-[#9CA0AB] text-sm">Cette action est irr&eacute;versible. Le highlight sera supprim&eacute; du terrain et de votre profil.</p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                disabled={deleting}
                                className="flex-1 py-2 px-4 rounded-lg border border-[#2A2D3A] text-[#B3B6C2] hover:bg-[#2A2D3A] transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleDeleteHighlight}
                                disabled={deleting}
                                className="flex-1 py-2 px-4 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors flex items-center justify-center gap-2"
                            >
                                {deleting ? <Loader className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </>
    );

    return content;
}

/** HLS-compatible video player modal for Profile */
function ProfileHighlightPlayer({ highlight, onClose }) {
    const videoRef = useRef(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !highlight.mux_playback_id) return;

        const src = `https://stream.mux.com/${highlight.mux_playback_id}.m3u8`;

        if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = src;
            video.play().catch(() => {});
            return;
        }

        let hls;
        import('https://cdn.jsdelivr.net/npm/hls.js@latest/+esm').then((mod) => {
            const Hls = mod.default;
            if (Hls.isSupported()) {
                hls = new Hls();
                hls.loadSource(src);
                hls.attachMedia(video);
                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    video.play().catch(() => {});
                });
            }
        }).catch(() => {
            video.src = src;
        });

        return () => { if (hls) hls.destroy(); };
    }, [highlight.mux_playback_id]);

    return (
        <div
            className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
                <video ref={videoRef} controls playsInline className="w-full rounded-lg" />
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 bg-[#6E2BFF] hover:bg-[#8B5CFF] rounded-full p-2 text-white"
                >
                    ✕
                </button>
                <div className="mt-4 text-white text-center">
                    <div className="flex items-center justify-center gap-2 text-sm text-[#B3B6C2]">
                        <Eye className="w-4 h-4" />
                        <span>{highlight.views_count || 0} vues</span>
                    </div>
                </div>
            </div>
        </div>
    );
}